import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { CheckCircle, TrendingUp, Users, Award, MessageSquare, Building2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/* Animated counter hook */
function useCountUp(target, duration = 1800, inView = false) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, target, duration]);

  return count;
}

/* Donut chart component */
function DonutChart({ percentage, label, inView }) {
  const radius = 65;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-40">
        <svg className="w-full h-full" viewBox="0 0 160 160" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="80" cy="80" r={radius} fill="none" stroke="#E2E8F0" strokeWidth="14" />
          <circle
            cx="80"
            cy="80"
            r={radius}
            fill="none"
            stroke="var(--sv-primary)"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={inView ? offset : circumference}
            style={{ transition: "stroke-dashoffset 1.8s cubic-bezier(0.4, 0, 0.2, 1)" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-3xl font-bold text-[var(--sv-secondary)] font-['Inter_Tight']">
            {inView ? percentage : 0}%
          </span>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium text-slate-500 text-center">{label}</p>
    </div>
  );
}

/* Funnel chart component */
function FunnelChart({ inView }) {
  const steps = [
    { label: "Coinvolti", value: 1000, width: "100%", color: "bg-blue-100 border-[#4B4DF7]/20" },
    { label: "Assessment completati", value: 900, width: "90%", color: "bg-[#4B4DF7]/20 border-[#4B4DF7]/30" },
    { label: "Top Talent identificati", value: 90, width: "50%", color: "bg-[#4B4DF7]/50 border-[#4B4DF7]/60" },
    { label: "Role-Ready (47%)", value: 42, width: "30%", color: "bg-[var(--sv-primary)] border-[#4B4DF7]/80" },
    { label: "In Development (53%)", value: 48, width: "34%", color: "bg-indigo-500 border-indigo-600" },
  ];

  return (
    <div className="space-y-3">
      {steps.map((step, i) => (
        <motion.div
          key={step.label}
          initial={{ opacity: 0, scaleX: 0 }}
          animate={inView ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 + i * 0.12 }}
          style={{ transformOrigin: "left" }}
          data-testid={`funnel-step-${i}`}
          className="funnel-step"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-500">{step.label}</span>
            <span className="text-sm font-bold text-[var(--sv-secondary)] font-['Inter_Tight']">
              ~{step.value.toLocaleString()}
            </span>
          </div>
          <div className="w-full h-8 bg-slate-50 rounded-lg overflow-hidden border border-slate-100">
            <div
              className={`h-full rounded-lg ${i >= 3 ? "text-white" : "text-blue-800"} flex items-center pl-3 text-xs font-medium transition-all duration-1000 ease-out`}
              style={{ width: inView ? step.width : "0%", background: i === 3 ? "var(--sv-primary)" : i === 4 ? "#6366F1" : undefined }}
              {...(i < 3 && { className: `h-full rounded-lg ${step.color} flex items-center pl-3 text-xs font-medium transition-all duration-1000 ease-out border`, style: { width: inView ? step.width : "0%" } })}
            >
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* Qualitative results */
const QUALITATIVE = [
  {
    icon: MessageSquare,
    title: "Costruzione pipeline interna",
    desc: "Per la prima volta, In's dispone di una mappatura oggettiva del potenziale sulla rete e può pianificare le aperture con la certezza di avere figure qualificate disponibili.",
  },
  {
    icon: TrendingUp,
    title: "Da logica reattiva a predittiva",
    desc: "Lo sviluppo delle persone è stato collegato direttamente al piano di espansione immobiliare: la pipeline interna diventa un asset di business che abilita la crescita.",
  },
  {
    icon: Building2,
    title: "Impatto culturale significativo",
    desc: "I partecipanti alle giornate in presenza sono tornati nei punti vendita raccontando l'esperienza, generando un effetto di contaminazione positiva e percezione di meritocrazia.",
  },
  {
    icon: Users,
    title: "Mobilità interna facilitata",
    desc: "Il progetto ha aperto opportunità non solo verticali (addetto vendita → Store Manager) ma anche trasversali tra divisioni, ad esempio verso ruoli logistici nei depositi.",
  },
];

export default function ResultsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  const completionCount = useCountUp(900, 1800, isInView);
  const potentialCount = useCountUp(90, 1500, isInView);

  return (
    <section
      id="risultati"
      data-testid="results-section"
      className="py-20 md:py-28 bg-[var(--sv-surface)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <Badge
            data-testid="results-badge"
            className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50 font-medium text-xs tracking-wide uppercase px-3 py-1"
          >
            Risultati
          </Badge>
          <h2
            data-testid="results-title"
            className="font-['Inter_Tight'] text-3xl md:text-4xl font-bold tracking-tight text-[var(--sv-secondary)]"
          >
            Key Metrics & Impatto
          </h2>
          <p className="mt-3 text-base text-slate-500 max-w-2xl mx-auto">
            I risultati quantitativi e qualitativi del progetto Skillvue x In's Mercato.
          </p>
        </motion.div>

        {/* Talent Pipeline Flow - Visual funnel */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <Card className="p-6 md:p-8 border-slate-100 bg-white" data-testid="talent-pipeline">
            <h3 className="font-['Inter_Tight'] font-semibold text-lg text-[var(--sv-secondary)] mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[var(--sv-primary)]" />
              Talent Pipeline
            </h3>
            
            {/* Flow visualization */}
            <div className="flex flex-col lg:flex-row items-stretch gap-4 lg:gap-0">
              {/* Step 1: Assessment */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="flex-1 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 relative"
              >
                <div className="absolute top-3 left-4 bg-slate-200 text-slate-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Step 1
                </div>
                <div className="pt-4">
                  <p className="text-5xl md:text-6xl font-bold text-[var(--sv-secondary)] font-['Inter_Tight']">
                    {completionCount}
                  </p>
                  <p className="text-sm font-semibold text-slate-600 mt-1">Assessment AI completati</p>
                  <p className="text-xs text-slate-400 mt-0.5">su ~1.000 collaboratori coinvolti</p>
                </div>
                {/* Arrow connector for desktop */}
                <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                  <div className="w-8 h-8 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </motion.div>

              {/* Step 2: Top Talent */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.35 }}
                className="flex-1 bg-gradient-to-br from-[#4B4DF7]/5 to-[#4B4DF7]/10 rounded-2xl p-6 relative lg:ml-4"
              >
                <div className="absolute top-3 left-4 bg-[#4B4DF7]/20 text-[var(--sv-primary)] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Step 2
                </div>
                <div className="pt-4">
                  <p className="text-5xl md:text-6xl font-bold text-[var(--sv-primary)] font-['Inter_Tight']">
                    {potentialCount}
                  </p>
                  <p className="text-sm font-semibold text-slate-600 mt-1">Top Talent identificati</p>
                  <p className="text-xs text-slate-400 mt-0.5">Top 10% emerso dall'assessment</p>
                </div>
                {/* Arrow connector for desktop */}
                <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                  <div className="w-8 h-8 bg-white border-2 border-[#4B4DF7]/30 rounded-full flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-[var(--sv-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </motion.div>

              {/* Step 3: Pipeline Split */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.5 }}
                className="flex-1 lg:ml-4"
              >
                <div className="h-full flex flex-col gap-3">
                  {/* Role-Ready */}
                  <div className="flex-1 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 relative border border-emerald-200/50">
                    <div className="absolute top-3 right-3 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Ready
                    </div>
                    <div className="flex items-end gap-3">
                      <p className="text-4xl md:text-5xl font-bold text-emerald-600 font-['Inter_Tight']">
                        47<span className="text-2xl">%</span>
                      </p>
                      <div className="pb-1">
                        <p className="text-sm font-semibold text-emerald-700">Role-Ready</p>
                        <p className="text-xs text-emerald-600/70">~42 profili pronti al ruolo</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* In Development */}
                  <div className="flex-1 bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-5 relative border border-amber-200/50">
                    <div className="absolute top-3 right-3 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> Growing
                    </div>
                    <div className="flex items-end gap-3">
                      <p className="text-4xl md:text-5xl font-bold text-amber-600 font-['Inter_Tight']">
                        53<span className="text-2xl">%</span>
                      </p>
                      <div className="pb-1">
                        <p className="text-sm font-semibold text-amber-700">In Development</p>
                        <p className="text-xs text-amber-600/70">~48 profili in upskilling</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Context note */}
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
              <Award className="w-4 h-4" />
              <span>Il 47% e 53% si riferiscono ai 90 Top Talent identificati</span>
            </div>
          </Card>
        </motion.div>

        {/* Charts row */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {/* Donut chart */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card
              data-testid="completion-rate-chart"
              className="p-6 md:p-8 border-slate-100 bg-white bento-card flex flex-col items-center"
            >
              <h3 className="font-['Inter_Tight'] font-semibold text-sm text-[var(--sv-secondary)] mb-6 self-start">
                Tasso di completamento
              </h3>
              <DonutChart
                percentage={95}
                label="Completamento al netto delle cause esterne (dimissioni, malattia)"
                inView={isInView}
              />
              <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                <span>48 non terminate per cause esterne</span>
              </div>
            </Card>
          </motion.div>

          {/* Funnel */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card
              data-testid="funnel-chart"
              className="p-6 md:p-8 border-slate-100 bg-white bento-card"
            >
              <h3 className="font-['Inter_Tight'] font-semibold text-sm text-[var(--sv-secondary)] mb-6">
                Il funnel di selezione
              </h3>
              <FunnelChart inView={isInView} />
            </Card>
          </motion.div>
        </div>

        {/* Qualitative results */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <h3
            data-testid="qualitative-title"
            className="font-['Inter_Tight'] text-xl md:text-2xl font-bold tracking-tight text-[var(--sv-secondary)] mb-6"
          >
            Impatto organizzativo e qualitativo
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {QUALITATIVE.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 12 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.6 + i * 0.1 }}
              >
                <Card
                  data-testid={`qualitative-card-${i}`}
                  className="p-5 border-slate-100 bg-white h-full bento-card"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#4B4DF7]/8 flex items-center justify-center mb-3">
                    <item.icon className="w-4.5 h-4.5 text-[var(--sv-primary)]" />
                  </div>
                  <h4 className="font-['Inter_Tight'] font-semibold text-sm text-[var(--sv-secondary)] mb-2">
                    {item.title}
                  </h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {item.desc}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
